// Next.js
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';

// Types
import type { SectionSlug } from 'interfaces/Section';
import type { Hub } from 'interfaces/Home';
import type { MenuItemProps } from 'interfaces/navigation/Header';
import type { ArticlePreview } from 'interfaces/content/articles/Articles';
import type { RelatedRanking } from 'interfaces/content/Ranking';
import type { PagePagination } from 'utils/miscUtils/calculatePageInfo';
import type { SectionFeaturedArticles } from 'api/Section/getFeaturedArticlesBySection';

// API
import getAllSections from 'api/Section/getAllSections';

// Components & Pages
import SectionHubPage from 'pages/SectionHubPage/SectionHubPage';
import MostPopularRR from 'components/MostPopularRR';

// Utils & Lib
import handleStaticPropsError from 'utils/handleStaticPropsError';
import { getSectionServerSideProps } from 'lib/section/sectionServerSideProps';

interface SectionProps {
  mostPopular: ArticlePreview[];
  section: Hub;
  dataLatest: ArticlePreview[];
  slug: string;
  subMenu?: MenuItemProps[];
  pageSize: number;
  pagination: PagePagination;
  relatedRankings?: RelatedRanking[];
  featuredArticles: SectionFeaturedArticles;
}

const SectionNextPage: NextPage<SectionProps> = ({
  mostPopular,
  section,
  dataLatest,
  subMenu,
  pagination,
  relatedRankings,
  featuredArticles,
}: SectionProps) => {
  return (
    <SectionHubPage
      data={section}
      dataLatest={dataLatest}
      pagination={pagination}
      subMenu={subMenu}
      relatedRankings={relatedRankings}
      featuredArticles={featuredArticles}
      sidebar={[
        mostPopular && (
          <MostPopularRR
            key={1}
            articles={mostPopular}
          />
        ),
      ]}
    />
  );
};

export default SectionNextPage;

interface Props {
  params: {
    slug: string;
  };
}

/**
 * Static props for regular sections - uses shared section logic
 * For preview section, a dedicated page with authentication is used instead
 */
export const getStaticProps: GetStaticProps = async (context) => {
  const { slug } = context.params as { slug: string };
  try {
    // Use the shared function but convert context to match GetServerSidePropsContext
    const mockServerContext = {
      req: {} as any,
      res: {} as any,
      params: context.params,
      query: {},
      resolvedUrl: `/section/${slug}`,
    };

    const result = await getSectionServerSideProps(mockServerContext, slug);

    if ('notFound' in result) {
      return result;
    }

    return {
      props: result.props,
      revalidate: 60,
    };
  } catch (error) {
    return handleStaticPropsError(error as Error);
  }
};

export const getStaticPaths: GetStaticPaths<{ slug: string }> = async () => {
  if (process.env.TEST) {
    return {
      fallback: 'blocking',
      paths: [],
    };
  }

  const { sections }: { sections: SectionSlug[] } = await getAllSections();
  const paths: Props[] = sections.map(
    (section) =>
      ({
        params: {
          slug: section.slug,
        },
      }) as Props,
  );
  return {
    fallback: 'blocking',
    paths,
  };
};
